import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { concat, isEmpty } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import type { IViewLocations } from 'src/interfaces';
import { generateQuery, requestCCApi } from 'src/utils';
import { queryKeys } from 'src/utils/queryKeys';

import { fetchViewChannels } from '../channels/useViewChannels';
import { fetchLocationGroups } from '../locationGroup';
import {
  mapViewLocations,
  mapViewLocationsByGroups,
} from '../schedules/helper';

export const useViewLocationsByGroupsWithoutPagination = (
  filters: string[] = [],
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.viewLocations.all(filters),
    queryFn: () => fetchViewLocationsByGroupsWithoutPagination(filters),
    enabled: enabled && !isEmpty(filters),
  });

export const fetchViewLocationsByGroupsWithoutPagination = async (
  filters: string[] = [],
) => {
  const query = generateQuery([], concat(filters, ['pageSize=0']));

  const url = `/viewLocationsByGroups${query}`;

  const response = await requestCCApi({ url });

  return mapViewLocationsByGroups(response);
};

export const useGetLocationsCountByGroup = (
  filters: string[] = [],
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.viewLocations.all([
      filters,
      'fetchGetLocationsByGroupsCount',
    ]),
    queryFn: () => fetchGetLocationsByGroupsCount(filters),
    enabled: enabled && !isEmpty(filters),
  });

export const fetchGetLocationsByGroupsCount = async (
  filters: string[] = [],
) => {
  // Extract locationGroups.id from filters (e.g., 'locationGroups.id=42')
  const locationGroupIdFilter = filters.find((filter) =>
    filter.startsWith('locationGroups.id='),
  );

  if (!locationGroupIdFilter) {
    throw new Error('locationGroups.id filter is required');
  }

  const locationGroupId = locationGroupIdFilter.split('=')[1];

  // Use new optimized count endpoint
  const url = `/locationGroupCount?locationGroups.id=${locationGroupId}`;

  const response = await requestCCApi({ url });

  // Endpoint returns { count: number }
  return response?.count ?? 0;
};

export const useViewLocationsByGroupsWithoutFilterAndPagination = (
  userRole?: AuthClassification | null,
  overrideQuerySetting?: UseQueryOptions<IViewLocations>,
) =>
  useQuery({
    queryKey: queryKeys.viewLocations.all('all'),
    queryFn: () =>
      fetchViewLocationsByGroupsWithoutFilterAndPagination(userRole),
    ...overrideQuerySetting,
  });

export const useViewLocationsByGroupsWithoutPaginationFilterByLocationCode = (
  userRole?: AuthClassification | null,
  overrideQuerySetting?: UseQueryOptions<IViewLocations>,
  filters: string[] = [],
) =>
  useQuery({
    queryKey: [
      'useViewLocationsByGroupsWithoutPaginationFilterByLocationCode',
      ...queryKeys.viewLocations.all(filters),
    ],
    queryFn: () =>
      fetchViewLocationsByGroupsWithoutPaginationFilterByLocationCode(
        userRole,
        filters,
      ),
    ...overrideQuerySetting,
  });

const fetchViewLocationsByGroupsWithoutFilterAndPagination = async (
  userRole?: AuthClassification | null,
) => {
  const [locationGroups, viewChannels, viewLocationsResponse] =
    await Promise.all([
      queryClient.fetchQuery({
        queryKey: queryKeys.locationGroups.all(),
        queryFn: () => fetchLocationGroups(),
      }),
      queryClient.fetchQuery({
        queryKey: queryKeys.viewChannels.all(),
        queryFn: fetchViewChannels,
      }),
      queryClient.fetchQuery({
        queryKey: queryKeys.viewLocationsByGroups.all(),
        queryFn: () =>
          fetchViewLocationsByGroupsWithIncludesAndWithoutPagination(userRole),
      }),
    ]);

  return mapViewLocations(viewLocationsResponse, viewChannels, locationGroups);
};

export const fetchViewLocationsByGroupsWithoutPaginationFilterByLocationCode =
  async (userRole?: AuthClassification | null, filters: string[] = []) => {
    const [locationGroups, viewChannels, viewLocationsResponse] =
      await Promise.all([
        queryClient.fetchQuery({
          queryKey: queryKeys.locationGroups.all(),
          queryFn: () => fetchLocationGroups(),
        }),
        queryClient.fetchQuery({
          queryKey: queryKeys.viewChannels.all(),
          queryFn: fetchViewChannels,
        }),
        queryClient.fetchQuery({
          queryKey: queryKeys.viewLocationsByGroups.all(filters),
          queryFn: () =>
            fetchViewLocationsByGroupsWithIncludesAndWithoutPagination(
              userRole,
              filters,
            ),
        }),
      ]);

    return mapViewLocations(
      viewLocationsResponse,
      viewChannels,
      locationGroups,
    );
  };

export const useViewLocationsByGroupsWithIncludesAndWithoutPagination = (
  userRole?: AuthClassification | null,
) =>
  useQuery({
    queryKey: queryKeys.viewLocationsByGroups.all(),
    queryFn: () =>
      fetchViewLocationsByGroupsWithIncludesAndWithoutPagination(userRole),
  });

export const fetchViewLocationsByGroupsWithIncludesAndWithoutPagination =
  async (userRole?: AuthClassification | null, filters: string[] = []) => {
    const queryParams = concat(filters, ['pageSize=0']);
    const viewLocationsByGroupsRes = await requestCCApi({
      url:
        userRole !== AuthClassification.OWNER_OPERATOR
          ? `/viewLocationsByGroups${generateQuery(
              ['locationTags', 'locationOwnerTags'],
              queryParams,
            )}`
          : `/viewLocationsByGroups?include=locationOwnerTags&pageSize=0`,
    });

    return viewLocationsByGroupsRes;
  };
